import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  Renderer2,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ComponentType } from '@angular/cdk/overlay';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isObservable, Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Home365Utils, PositionUtil } from "@home365/common-classes";
import { ComponentPortal, Portal } from "@angular/cdk/portal";

export declare type ButtonExecuteResponse<T = unknown> = { successes: boolean, massage: string, displayMassage: boolean, res?: T };
export type Home365PopupButtonExecuteFunction = () => Observable<ButtonExecuteResponse> | ButtonExecuteResponse;

export interface Home365PopupButtonModel {
  text: string,
  classes: string,
  isCloseButton?: boolean,
  execute?: Home365PopupButtonExecuteFunction
}

export interface Home365PopupDataModel<T = null> {
  // by default enter will press the First button
  hideSubtitle?: boolean;
  buttons?: Home365PopupButtonModel[],
  component?: ComponentType<unknown>,
  portal?: Portal<unknown>,
  data?: Home365PopupChildrenDataModel<T> | null,
  headerClasses?: string;
  subtitle?: string;
  massageDisplayTimerSeconds?: number,
  title: string,
  hideHeader?: boolean,
  red?: boolean,
  minimizeTitle?: boolean, // set title font to 21px
  blue?: boolean,
  withTriangle?: boolean,
  withBoxShadow?: boolean
}

export class Home365PopupChildrenDataModel<T> {
  data?: T;
  constructor(data?: T) {
    this.data = data;
  }
}
export class Home365PopupActions {
  actionResult!: (successes: boolean, response: unknown) => void;
  close!: () => void;
  changePopupHeader!: (title?: string, subTitle?: string, classes?: string) => void;
  displayMsg!: (successes: boolean, msg: string) => void;
  onEnterPress!: Observable<unknown>; // will emit when the parent element detects 'enter' press
  showLoader!: () => void;
  setCanClose!: (canClose: boolean) => void;
  execute!: (obs$: Observable<{ actionSuccesses: boolean, response: string }>) => void;
  home365PopupElementRef!: ElementRef
  setTriangle!: (target: HTMLElement) => void;
}


export interface Home365PopupResponse<T = unknown> {
  actionSuccess: boolean, // Did the action sussed
  wasClosed: boolean, // Did the user close the modal,
  response: T
}

/**
 * General modal that can display any component.
 * @Component_info
 * @Origen - Sprint 41
 * @Modifications -
 * 1.
 */

@Component({
  selector: 'lib-home365-popup',
  templateUrl: './home365-popup.component.html',
  styleUrls: ['./home365-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'max-height-container'
  }
})
export class Home365PopupComponent<T> implements OnDestroy {
  actionCompleted = false;
  actionData!: { successes: boolean, massage: string | null, response: unknown };
  canClose = true;
  componentPortal?: Portal<unknown>;
  headerClasses: string;
  // minimizeTitle: boolean;
  modalTitle: string;
  modalSubTitle?: string;
  injector?: Injector;
  showComponent: boolean;
  onEnterPress?: Subject<unknown>;
  executing = false;
  hideHeader = false;
  @HostBinding('class.mat-dialog-container') isTransparent = false;
  @ViewChild('triangle', { static: true }) triangleRef!: ElementRef<HTMLElement>;
  constructor(@Inject(MAT_DIALOG_DATA) public data: Home365PopupDataModel<T>, private dialog: MatDialogRef<Home365PopupComponent<T>>,
    injectorParent: Injector, private cd: ChangeDetectorRef, private el: ElementRef, private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef, private elementRef: ElementRef<HTMLElement>) {
    this.dialog.addPanelClass('home365-mat-dialog-no-padding');
    if (!data.data) {
      data.data = null;
    }
    if (this.data.withTriangle) {
      this.dialog.addPanelClass('home-365-transparent-modal');
      this.isTransparent = true;
    }
    if (this.data.withBoxShadow) {
      this.dialog.addPanelClass('home-365-box-shadow-modal');
    }
    this.modalTitle = data.title;
    this.modalSubTitle = data.subtitle;
    this.headerClasses = data.headerClasses ?? '';
    this.headerClasses += ` ${data.red ? 'bk-red' : data.blue ? 'bk-defult' : ''}`
    if (data.hideSubtitle) {
      this.headerClasses += ' no-min-height';
    }
    if (data.hideHeader) {
      this.hideHeader = data.hideHeader;
    }
    if (data.component || data.portal) {
      this.onEnterPress = new Subject<unknown>();
      this.showComponent = true;
      const actions: Home365PopupActions = new Home365PopupActions();
      actions.actionResult = this.actionResult;
      actions.close = this.close;
      actions.changePopupHeader = this.changePopupHeader;
      actions.displayMsg = this.displayMsg;
      actions.execute = this.execute;
      actions.home365PopupElementRef = el;
      actions.onEnterPress = this.onEnterPress.asObservable();
      actions.showLoader = this.showLoader;
      actions.setCanClose = this.setCanClose;
      actions.setTriangle = this.setTriangle;
      this.injector = Injector.create({
        providers: [
          { provide: Home365PopupChildrenDataModel, useValue: data.data },
          { provide: Home365PopupActions, useValue: actions }
        ],
        parent: injectorParent
      });
      this.componentPortal = data.component ? new ComponentPortal<unknown>(data.component, viewContainerRef, this.injector) :
        data.portal;
    } else {
      this.showComponent = false;
    }
    // this.dialog.disableClose = true;
  }

  ngOnDestroy() {
    this.onEnterPress?.complete();
  }

  @HostListener('keyup.enter') onEnter() {
    if (this.showComponent) {
      this.onEnterPress?.next(null);
    } else if (Home365Utils.isArrNotEmpty(this.data.buttons) && !!this.data.buttons?.[0]) {
      this.buttonClicked(this.data.buttons[0]);
    }
  }
  setTriangle = (target: HTMLElement) => {
    const posUtil = new PositionUtil(),
      targetPos = posUtil.getPositionData(target),
      triangle = posUtil.getPositionData(this.triangleRef.nativeElement).boundingClientRect,
      outOfBoxPosition = '-28px',
      containerPos = posUtil.getPositionData(this.elementRef.nativeElement).boundingClientRect;
    if (targetPos.boundingClientRect.top < containerPos.top) {
      this.renderer.setStyle(this.triangleRef.nativeElement, 'transform', 'rotateZ(90deg)');
      this.renderer.setStyle(this.triangleRef.nativeElement, 'top', outOfBoxPosition);
      this.renderer.setStyle(this.triangleRef.nativeElement, 'left',
        targetPos.values.centerHorizontal - containerPos.left - triangle.width / 2 + 'px');
    } else if (targetPos.boundingClientRect.left < containerPos.left) {
      this.renderer.setStyle(this.triangleRef.nativeElement, 'left', outOfBoxPosition);
      this.renderer.setStyle(this.triangleRef.nativeElement, 'top',
        targetPos.values.centerVertical - containerPos.top - triangle.height / 2 + 'px');
    }
  }
  close = () => {
    const res: Home365PopupResponse =
      this.actionData ? { wasClosed: true, actionSuccess: this.actionData.successes, response: this.actionData.response } :
        { wasClosed: true, actionSuccess: false, response: null };
    this.dialog.close(res);
  }
  changePopupHeader = (title?: string, subTitle?: string, classes?: string) => {
    if (title) {
      this.modalTitle = title;
    }
    if (subTitle) {
      this.modalSubTitle = subTitle;
    }
    if (classes) {
      this.headerClasses = classes;
    }
    this.cd.markForCheck();
  }
  actionResult = (successes: boolean, response?: unknown) => {
    this.actionData = { successes, massage: null, response };
  }
  setCanClose = (canClose: boolean) => {
    this.canClose = canClose;
    this.dialog.disableClose = !canClose;
    // this.dialog.backdropClick().subscribe()
    this.cd.detectChanges();
  }
  displayMsg = (successes: boolean, massage: string, response: unknown = null) => {
    this.actionCompleted = true;
    this.actionData = { successes, massage, response };
    this.cd.detectChanges();
    if (!this.data.massageDisplayTimerSeconds) {
      this.close();
    } else {
      setTimeout(() => {
        this.close()
      }, this.data.massageDisplayTimerSeconds * 1000);
      if (this.canClose) {
        this.dialog.backdropClick().subscribe(() => {
          this.close();
        });
      }
    }
  }
  showLoader = () => {
    this.executing = true;
    this.actionCompleted = false;
    this.cd.detectChanges();
  }

  buttonClicked(button: Home365PopupButtonModel) {
    if (button.isCloseButton) {
      this.close();
    } else if (button.execute) {
      this.executing = true;
      const response = button?.execute?.();
      if (isObservable(response)) {
        response.subscribe(res => this._handleButtonExecute(res));
      } else {
        this._handleButtonExecute(response);
      }
    } else {
      this.close();
    }
  }
  private _handleButtonExecute(res: ButtonExecuteResponse) {
    if (res.displayMassage) {
      this.displayMsg(res.successes, res.massage)
    } else {
      this.actionData = { successes: res.successes, massage: res.massage, response: res };
      this.close();
    }
  }
  execute = (obs$: Observable<{ actionSuccesses: boolean, response: string }>) => {
    this.showLoader();
    obs$.subscribe(res => this.displayMsg(res.actionSuccesses, res.response));
  }
}

